import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Calendar, UsersRound, ExternalLink, FileText, Loader2, MessageSquare } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import Layout from '@/components/layout/Layout';
import { useAuth } from '@/context/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useActionBarConfig } from '@/contexts/ActionBarContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { formatDate, formatScheduleLabel, formatDeNumber } from '@/lib/utils';
import { visibleCourseBookings } from '@/lib/courseBookings';
import { writeSelectedRecord } from '@/lib/selectedRecord';
import { statusLabel } from '@/lib/deLabels';
import PhoneLink from '@/components/PhoneLink';
import { contactRoleLabel } from '@/lib/contactRoles';
import { CustomFieldsSection } from '@/components/customfields/CustomFieldsSection';
import PersonRemindersPanel from '@/pages/Dashboard/PersonRemindersPanel';
import PersonAppKennungen from '@/components/app/PersonAppKennungen';
import StudentDiscountsPanel from '@/components/students/StudentDiscountsPanel';
import StudentPrepaidCards from '@/pages/Students/StudentPrepaidCards';

const STUDENT_TABS = ['info', 'enrollments', 'cards', 'billing', 'discounts', 'guardians', 'family', 'notes', 'reminders', 'customfields', 'app'];

function tabFromSearch(searchParams, isTeacher) {
  const requested = searchParams.get('tab');
  if (!STUDENT_TABS.includes(requested)) return 'info';
  if (isTeacher && ['billing', 'discounts', 'family'].includes(requested)) return 'info';
  return requested;
}

export default function StudentDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isSuperAdmin } = useTenant();
  const roleNames = new Set((user?.roles || []).map((role) => role.name));
  const isTeacher = (roleNames.has('teacher') || roleNames.has('lehrer'))
    && !isSuperAdmin
    && !['admin', 'secretary', 'sekretariat', 'verwaltung', 'management']
      .some((role) => roleNames.has(role));
  const [student, setStudent] = useState(null);
  const [family, setFamily] = useState(null);
  const [siblingDiscount, setSiblingDiscount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [activeTab, setActiveTab] = useState(() => tabFromSearch(searchParams, isTeacher));

  const payerG = (student?.guardians || []).map((sg) => sg.guardian).filter(Boolean)
    .sort((a, b) => (b.is_payer === true) - (a.is_payer === true))[0] || null;
  useActionBarConfig({
    selected: payerG
      ? { kind: 'guardian', id: payerG.id, name: `${payerG.first_name} ${payerG.last_name}`, email: payerG.email, phone: payerG.phone }
      : (student ? { kind: 'student', id: student.id, name: `${student.first_name} ${student.last_name}` } : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student?.id, payerG?.id]);
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadInvoicePreview = async () => {
    if (preview || previewLoading) return;
    setPreviewLoading(true);
    try {
      const res = await api.get(`/students/${id}/invoice-preview`);
      setPreview(res.data);
    } catch (e) {
      toast.error('Vorschau konnte nicht geladen werden');
    } finally {
      setPreviewLoading(false);
    }
  };

  const openInvoicePreview = async () => {
    setPreviewOpen(true);
    await loadInvoicePreview();
  };

  useEffect(() => {
    if (isTeacher && ['billing', 'discounts', 'family'].includes(activeTab)) {
      setActiveTab('info');
    }
  }, [isTeacher, activeTab]);

  useEffect(() => {
    if (!student?.id) return;
    const bookings = visibleCourseBookings(student.enrollments);
    const record = {
      studentId: student.id,
      guardianId: payerG?.id || null,
      familyId: student.family_id || null,
    };
    if (bookings.length === 1) {
      record.enrollmentId = bookings[0].id;
      record.courseId = bookings[0].course_id || null;
    }
    writeSelectedRecord(record);
  }, [student, payerG]);

  useEffect(() => {
    if (!isTeacher && activeTab === 'billing') loadInvoicePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id, isTeacher]);

  useEffect(() => {
    setPreview(null);
  }, [id]);

  useEffect(() => {
    if (id !== 'new') {
      loadStudent();
    } else {
      setLoading(false);
    }
  }, [id]);

  const loadStudent = async () => {
    try {
      const response = await api.get(`/students/${id}`);
      setStudent(response.data);
      
      // Load family data if student has family_id
      if (!isTeacher && response.data.family_id) {
        try {
          const familyRes = await api.get(`/families/${response.data.family_id}`);
          setFamily(familyRes.data);
        } catch (e) {
          console.log('No family data');
        }
        
        // Load sibling discount suggestion
        try {
          const discountRes = await api.get(`/families/sibling-discount/${id}`);
          setSiblingDiscount(discountRes.data);
        } catch (e) {
          console.log('No sibling discount');
        }
      }
    } catch (error) {
      toast.error('Fehler beim Laden des Schülers');
      navigate('/students');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: { label: 'Aktiv', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      inactive: { label: 'Inaktiv', className: 'bg-slate-100 text-slate-700' },
      paused: { label: 'Pausiert', className: 'bg-amber-50 text-amber-700 border-amber-200' },
    };
    const config = variants[status] || variants.active;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-red"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="student-name">
                {student?.first_name} {student?.last_name}
              </h1>
              {getStatusBadge(student?.status)}
            </div>
            {!isTeacher && (
            <div className="flex gap-2">
              <Button
                onClick={openInvoicePreview}
                data-testid="invoice-preview-button"
                variant="outline"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              >
                <FileText className="w-4 h-4 mr-2" /> Rechnungs-Vorschau
              </Button>
              <Button
                onClick={() => navigate(`/students/${id}/edit`)}
                data-testid="edit-student-button"
                className="bg-slate-900 text-white hover:bg-slate-800"
              >
                Bearbeiten
              </Button>
            </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-500">E-Mail</p>
                  <p className="text-sm text-slate-900">{student?.email || '-'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-500">Telefon</p>
                  <p className="text-sm text-slate-900">
                    {student?.phone ? <PhoneLink phone={student.phone} /> : '-'}
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-500">Geburtsdatum</p>
                  <p className="text-sm text-slate-900">{student?.date_of_birth ? formatDate(student.date_of_birth) : '-'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-500">Adresse</p>
                  <p className="text-sm text-slate-900">
                    {student?.address ? (
                      <>
                        {student.address}<br />
                        {student.postal_code} {student.city}
                      </>
                    ) : '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full min-w-0">
          <TabsList className="border-b border-slate-200 w-full h-auto flex flex-wrap justify-start rounded-none bg-transparent p-0 gap-1">
            <TabsTrigger value="info" data-testid="tab-info" className="shrink-0">Stammdaten</TabsTrigger>
            <TabsTrigger value="enrollments" data-testid="tab-enrollments" className="shrink-0">Belegungen</TabsTrigger>
            <TabsTrigger value="cards" data-testid="tab-cards" className="shrink-0">Karten</TabsTrigger>
            {!isTeacher && (
              <TabsTrigger value="billing" data-testid="tab-billing" className="shrink-0">Abrechnung</TabsTrigger>
            )}
            {!isTeacher && (
              <TabsTrigger value="discounts" data-testid="tab-discounts" className="shrink-0">Rabatte & Zuschläge</TabsTrigger>
            )}
            <TabsTrigger value="guardians" data-testid="tab-guardians" className="shrink-0">Erziehungsberechtigte</TabsTrigger>
            {!isTeacher && (
            <TabsTrigger value="family" data-testid="tab-family" className="shrink-0">
              Familie
              {family && <Badge className="ml-2 text-xs">{family.member_count}</Badge>}
            </TabsTrigger>
            )}
            <TabsTrigger value="notes" data-testid="tab-notes" className="shrink-0">Notizen</TabsTrigger>
            <TabsTrigger value="reminders" data-testid="tab-reminders" className="shrink-0" title="Nachrichten / Erinnerungen">
              <MessageSquare className="w-3.5 h-3.5 mr-1 inline shrink-0" />
              Erinnerungen
            </TabsTrigger>
            <TabsTrigger value="customfields" data-testid="tab-customfields" className="shrink-0">Zusatzfelder</TabsTrigger>
            {!isTeacher && (
              <TabsTrigger value="app" data-testid="tab-app" className="shrink-0">App</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="info" className="mt-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Stammdaten</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4" data-testid="student-master-data">
                <div>
                  <p className="text-sm font-medium text-slate-500">Geschlecht</p>
                  <p className="text-sm text-slate-900">{{ m: 'Männlich', w: 'Weiblich', d: 'Divers' }[student?.gender] || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">Kassenzeichen</p>
                  <p className="text-sm text-slate-900">{student?.imikel_kassenzeichen || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">Filiale (GKZ)</p>
                  <p className="text-sm text-slate-900">{student?.gkz || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">Angelegt am</p>
                  <p className="text-sm text-slate-900">{student?.created_at ? formatDate(student.created_at) : '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">Aufmerksam geworden durch</p>
                  <p className="text-sm text-slate-900">{student?.attention_source || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">Kurznotiz</p>
                  <p className="text-sm text-slate-900">{student?.short_note || '-'}</p>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="enrollments" className="mt-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Kursbelegungen</h2>
              {visibleCourseBookings(student?.enrollments).length > 0 ? (
                <div className="space-y-4" data-testid="student-enrollments">
                  {visibleCourseBookings(student.enrollments).map((enrollment) => (
                    <button
                      key={enrollment.id}
                      type="button"
                      data-testid={`student-enrollment-${enrollment.id}`}
                      onClick={() => navigate(`/unterrichtsverwaltung?enrollment_id=${enrollment.id}`)}
                      className="w-full text-left p-4 bg-slate-50 rounded-lg flex items-center justify-between hover:bg-slate-100"
                    >
                      <div>
                        <p className="font-medium text-slate-900">{formatScheduleLabel(enrollment.course_name)}</p>
                        <p className="text-sm text-slate-600 mt-1">
                          Seit: {enrollment.start_date ? formatDate(enrollment.start_date) : '-'}
                          {enrollment.end_date && ` bis ${formatDate(enrollment.end_date)}`}
                        </p>
                      </div>
                      <Badge className={
                        enrollment.status === 'active' 
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-slate-100 text-slate-700'
                      }>
                        {statusLabel(enrollment.status)}
                      </Badge>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Keine Kursbelegungen vorhanden</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="cards" className="mt-6" data-testid="student-cards-tab">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-1">Kartenkontingent</h2>
              <p className="text-sm text-slate-500 mb-4">
                Verbrauch aus den bestätigten Einträgen in den Anwesenheitslisten.
              </p>
              <StudentPrepaidCards studentId={id} />
            </div>
          </TabsContent>

          <TabsContent value="billing" className="mt-6" data-testid="student-billing-tab">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Abrechnung</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    Monatsvorschau inkl. Familien-/Mehrfächerermäßigung und Links zu den Belegungen.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openInvoicePreview}
                  data-testid="student-billing-open-preview"
                >
                  <FileText className="w-4 h-4 mr-1.5" />
                  Vorschau-Dialog
                </Button>
              </div>

              <InvoicePreviewBody loading={previewLoading} preview={preview} />

              <div>
                <h3 className="text-sm font-semibold text-slate-800 mb-2">Belegungen öffnen</h3>
                {visibleCourseBookings(student?.enrollments).length === 0 ? (
                  <p className="text-sm text-slate-500">Keine Belegungen vorhanden</p>
                ) : (
                  <div className="space-y-2">
                    {visibleCourseBookings(student.enrollments).map((enrollment) => (
                      <div
                        key={enrollment.id}
                        className="flex items-center justify-between gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100"
                      >
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 truncate">
                            {formatScheduleLabel(enrollment.course_name)}
                          </p>
                          <p className="text-xs text-slate-500">
                            {statusLabel(enrollment.status)}
                            {enrollment.start_date ? ` · ab ${formatDate(enrollment.start_date)}` : ''}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`student-billing-open-enrollment-${enrollment.id}`}
                          onClick={() => navigate(`/unterrichtsverwaltung?enrollment_id=${enrollment.id}`)}
                        >
                          <ExternalLink className="w-3.5 h-3.5 mr-1" />
                          Unterrichtsverwaltung
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="discounts" className="mt-6">
            <StudentDiscountsPanel
              studentId={id}
              enrollments={student?.enrollments || []}
            />
          </TabsContent>

          <TabsContent value="guardians" className="mt-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Erziehungsberechtigte</h2>
              {student?.guardians && student.guardians.length > 0 ? (
                <div className="space-y-4">
                  {student.guardians.map((sg) => (
                    <div key={sg.id} className="p-4 bg-slate-50 rounded-lg">
                      <p className="font-medium text-slate-900">
                        {sg.guardian.first_name} {sg.guardian.last_name}
                      </p>
                      <p className="text-sm text-slate-600 mt-1">{sg.guardian.email}</p>
                      <p className="text-sm text-slate-600">{sg.guardian.phone}</p>
                      <p className="text-xs text-slate-500 mt-2">Beziehung: {contactRoleLabel(sg.relationship_type)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Keine Erziehungsberechtigten hinterlegt</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="customfields" className="mt-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Zusatzfelder</h3>
              <CustomFieldsSection entityType="student" entityId={id} />
            </div>
          </TabsContent>

          <TabsContent value="notes" className="mt-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Notizen</h2>
              {student?.notes ? (
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{student.notes}</p>
              ) : (
                <p className="text-sm text-slate-500">Keine Notizen vorhanden</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="reminders" className="mt-6">
            <PersonRemindersPanel
              personType="student"
              personId={id}
              personLabel={student ? `${student.last_name}, ${student.first_name}` : ''}
            />
          </TabsContent>

          {!isTeacher && (
            <TabsContent value="app" className="mt-6">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">App</h2>
                <PersonAppKennungen personType="student" personId={id} />
              </div>
            </TabsContent>
          )}

          <TabsContent value="family" className="mt-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                  <UsersRound className="h-5 w-5" />
                  Familie
                </h2>
                {family && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/families?id=${family.id}`)}
                  >
                    Zur Familienverwaltung
                  </Button>
                )}
              </div>
              
              {family ? (
                <div className="space-y-6">
                  {/* Family Info */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h3 className="font-medium text-slate-900">{family.name}</h3>
                    {family.primary_address && (
                      <p className="text-sm text-slate-600 mt-1">
                        {family.primary_address}, {family.primary_postal_code} {family.primary_city}
                      </p>
                    )}
                    <p className="text-sm text-slate-500 mt-2">
                      {family.member_count} Familienmitglieder • {family.active_enrollments_count} aktive Belegungen
                    </p>
                  </div>
                  
                  {/* Sibling Discount Suggestion */}
                  {siblingDiscount && (
                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-emerald-100 rounded-full">
                          <UsersRound className="h-4 w-4 text-emerald-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-emerald-800">Geschwisterrabatt möglich</h4>
                          <p className="text-sm text-emerald-700 mt-1">
                            {siblingDiscount.sibling_count} Geschwister mit aktiven Belegungen
                          </p>
                          <p className="text-sm font-medium text-emerald-800 mt-2">
                            Vorschlag: {siblingDiscount.suggested_discount_name} ({siblingDiscount.suggested_discount_percent}%)
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Family Members */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
                      Familienmitglieder
                    </h3>
                    <div className="space-y-2">
                      {family.members?.map((member) => (
                        <div 
                          key={member.id} 
                          className={`p-3 rounded-lg flex items-center justify-between ${
                            member.id === id ? 'bg-brand-red/5 border border-brand-red/20' : 'bg-slate-50'
                          }`}
                        >
                          <div>
                            <p className="font-medium text-slate-900">
                              {member.first_name} {member.last_name}
                              {member.id === id && (
                                <Badge className="ml-2 text-xs">Dieser Schüler</Badge>
                              )}
                            </p>
                            <p className="text-sm text-slate-500">{member.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {member.active_enrollments > 0 && (
                              <Badge variant="outline">{member.active_enrollments} Belegungen</Badge>
                            )}
                            {member.id !== id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/students/${member.id}`)}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500">
                  Dieser Schüler ist keiner Familie zugeordnet. 
                  <Button 
                    variant="link" 
                    className="px-1 h-auto"
                    onClick={() => navigate('/families')}
                  >
                    Familien automatisch erkennen
                  </Button>
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <InvoicePreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        loading={previewLoading}
        preview={preview}
      />
    </Layout>
  );
}

function eur(n) {
  return formatDeNumber(n);
}

function lineAdultSurcharge(li) {
  return Number(li.adult_surcharge) || 0;
}

function geld(n) {
  const v = Number(n);
  return Number.isFinite(v) ? v : 0;
}

function rabattZeilen(li) {
  return [
    [li.student_discount_labels, li.student_discount_amount],
    [li.gema_discount_labels, li.gema_discount_amount],
    [li.zusatz_discount_labels, li.zusatz_discount_amount],
  ].flatMap(([labels, amount]) => {
    const wert = geld(amount);
    const namen = (labels || []).filter(Boolean);
    if (wert <= 0 && namen.length === 0) return [];
    return [{ name: namen.join(' · ') || 'Rabatt', amount: wert }];
  });
}

/** Dieselben Bausteine wie die Berechnung: Gebühr, benannter Rabatt, Netto, Monat. */
function abrechnungszeilen(li) {
  const tuition = li.biweekly_factor_amount ?? li.biweekly_base_amount ?? li.base_amount;
  const rows = [{ label: 'Unterrichtsgebühr', amount: geld(tuition) }];
  const adult = geld(li.adult_surcharge);
  if (adult > 0) rows.push({ label: 'Erwachsenenzuschlag', amount: adult });
  for (const rabatt of rabattZeilen(li)) {
    if (rabatt.name === (li.gema_discount_labels || []).filter(Boolean).join(' · ') && geld(li.gema_discount_amount) > 0) {
      continue;
    }
    if (rabatt.name === (li.zusatz_discount_labels || []).filter(Boolean).join(' · ') && geld(li.zusatz_discount_amount) > 0) {
      continue;
    }
    rows.push({ label: `Rabatt ${rabatt.name}`, amount: -rabatt.amount });
  }
  const gemaBase = geld(li.gema_fee);
  const gemaOff = geld(li.gema_discount_amount);
  if (gemaBase > 0 || gemaOff > 0 || li.gema_targeted) {
    const namen = (li.gema_discount_labels || []).filter(Boolean).join(' · ');
    const hinweis = namen && gemaOff > 0 ? ` · Rabatt ${namen} −${eur(gemaOff)} €` : '';
    rows.push({ label: `GEMA-/Lizenzgebühr${hinweis}`, amount: Math.max(gemaBase - gemaOff, 0) });
  }
  const socialBase = geld(li.social_fund);
  const adminBase = geld(li.biweekly_admin_fee);
  const zusatzOff = geld(li.zusatz_discount_amount);
  if (socialBase > 0 || adminBase > 0 || zusatzOff > 0 || li.zusatz_targeted) {
    const namen = (li.zusatz_discount_labels || []).filter(Boolean).join(' · ');
    const hinweis = namen && zusatzOff > 0 ? ` · Rabatt ${namen} −${eur(zusatzOff)} €` : '';
    const socialOff = Math.min(zusatzOff, socialBase);
    const adminOff = Math.min(Math.max(zusatzOff - socialOff, 0), adminBase);
    if (socialBase > 0 || socialOff > 0) {
      rows.push({ label: `Sozialfonds${hinweis}`, amount: Math.max(socialBase - socialOff, 0) });
    }
    if (adminBase > 0 || adminOff > 0) {
      rows.push({ label: `Verwaltung 14-täglich${socialBase > 0 ? '' : hinweis}`, amount: Math.max(adminBase - adminOff, 0) });
    }
    if (zusatzOff > 0 && socialBase <= 0 && adminBase <= 0) {
      rows.push({ label: `Zusatz${hinweis}`, amount: 0 });
    }
  }
  rows.push({ label: 'Monatlicher Betrag', amount: geld(li.line_total) });
  return rows;
}

export function InvoicePreviewBody({ loading, preview }) {
  if (loading) {
    return (
      <div className="py-10 flex flex-col items-center gap-2 text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin" />
        <p className="text-sm">Berechnung läuft…</p>
      </div>
    );
  }

  if (!preview) {
    return (
      <p className="text-sm text-slate-500 italic">Noch keine Vorschau geladen.</p>
    );
  }

  const noteNoise = /^(GEMA|Erwachsene|Mehrfächer|Geschwister|Sozialfonds)/i;

  return (
    <div className="space-y-4" data-testid="invoice-preview-body">
      {(preview.line_items || []).length === 0 && (
        <p className="text-sm text-slate-500 italic">
          Keine aktiven monatlichen Belegungen mit Tarif gefunden.
        </p>
      )}

      {(preview.line_items || []).length > 0 && (
        <div className="border rounded-md overflow-x-auto min-w-0">
          <table className="w-full table-fixed text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="text-left p-2 w-[32%]">Schüler / Kurs</th>
                <th className="text-right p-2 w-[12%]">Tarif</th>
                <th className="text-right p-2 w-[10%]">GEMA</th>
                <th className="text-right p-2 w-[14%]">Zuschlag</th>
                <th className="text-right p-2 w-[18%]">Rabatt</th>
                <th className="text-right p-2 w-[14%]">Total</th>
              </tr>
            </thead>
            <tbody>
              {preview.line_items.map((li) => {
                // API: biweekly_factor_amount; älteres Feld biweekly_base_amount nur als Fallback
                const tariffAmt = li.biweekly_factor_amount ?? li.biweekly_base_amount ?? li.base_amount;
                const adultAmt = lineAdultSurcharge(li);
                const socialAmt = Number(li.social_fund) || 0;
                const adminAmt = Number(li.biweekly_admin_fee) || 0;
                const hasZuschlag = adultAmt > 0 || socialAmt > 0 || adminAmt > 0;
                const rabatte = rabattZeilen(li);
                const studentDiscLabels = rabatte.map((row) => row.name);
                const familyDiscPct = Number(li.family_discount_pct) || 0;
                const familyDiscAmt = Number(li.family_discount_amount) || 0;
                const hasRabatt = familyDiscPct > 0 || rabatte.length > 0;
                const gemaBase = Number(li.gema_fee) || 0;
                const gemaOff = Number(li.gema_discount_amount) || 0;
                const gemaNet = Math.max(gemaBase - gemaOff, 0);
                const showGema = gemaBase > 0 || gemaOff > 0 || li.gema_targeted;
                const zeilen = abrechnungszeilen(li);
                // Spalteninhalte nicht doppelt unter dem Kursnamen wiederholen
                const notesOther = (li.notes || '')
                  .split(/\s*[•·]\s*/)
                  .map((p) => p.trim())
                  .filter(Boolean)
                  .filter((part) => !studentDiscLabels.some((lbl) => part.startsWith(lbl)))
                  .filter((part) => !noteNoise.test(part))
                  .join(' • ');
                return (
                <tr key={li.enrollment_id} className="border-t">
                  <td className="p-2 min-w-0 align-top">
                    <p className="font-medium text-slate-800 break-words">{li.student_name}</p>
                    <p className="text-[11px] text-slate-500 break-words">{formatScheduleLabel(li.course_name)}</p>
                    <ul className="mt-1 space-y-0.5 text-[11px] text-slate-600" data-testid={`billing-statement-${li.enrollment_id}`}>
                      {zeilen.map((row) => (
                        <li key={row.label} className={row.label === 'Monatlicher Betrag' ? 'font-semibold text-slate-800' : ''}>
                          {row.label}: {row.amount < 0 ? '−' : ''}{eur(Math.abs(row.amount))} €
                        </li>
                      ))}
                    </ul>
                    {notesOther && (
                      <p className="text-[10px] text-slate-400 mt-0.5 break-words">{notesOther}</p>
                    )}
                  </td>
                  <td className="p-2 text-right whitespace-nowrap align-top">
                    <p>{eur(tariffAmt)} €</p>
                    {li.biweekly && (
                      <p className="text-[11px] text-slate-500">14-tägig</p>
                    )}
                  </td>
                  <td className="p-2 text-right whitespace-nowrap align-top">{showGema ? `${eur(gemaNet)} €` : '—'}</td>
                  <td className="p-2 text-right align-top">
                    {hasZuschlag ? (
                      <div className="space-y-0.5 text-slate-700">
                        {adultAmt > 0 && (
                          <div>
                            <p className="font-medium whitespace-nowrap">+{eur(adultAmt)} €</p>
                            <p className="text-[11px] text-slate-500">Erwachsen</p>
                          </div>
                        )}
                        {socialAmt > 0 && (
                          <div>
                            <p className="font-medium whitespace-nowrap">+{eur(socialAmt)} €</p>
                            <p className="text-[11px] text-slate-500">Sozialfonds</p>
                          </div>
                        )}
                        {adminAmt > 0 && (
                          <div>
                            <p className="font-medium whitespace-nowrap">+{eur(adminAmt)} €</p>
                            <p className="text-[11px] text-slate-500">VW 14-tg.</p>
                          </div>
                        )}
                      </div>
                    ) : '—'}
                  </td>
                  <td className="p-2 text-right min-w-0 align-top">
                    {hasRabatt ? (
                      <div className="space-y-0.5 text-rose-700 break-words">
                        {familyDiscPct > 0 && (
                          <p className="whitespace-nowrap">-{eur(familyDiscAmt)} € ({familyDiscPct}%)</p>
                        )}
                        {rabatte.map((row) => (
                          <div key={row.name}>
                            <p className="whitespace-nowrap">−{eur(row.amount)} €</p>
                            <p className="text-[11px] font-normal break-words">Rabatt {row.name}</p>
                          </div>
                        ))}
                      </div>
                    ) : '—'}
                  </td>
                  <td className="p-2 text-right font-semibold whitespace-nowrap align-top">{eur(li.line_total)} €</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-slate-50 border rounded-md p-4 space-y-1 text-sm">
        <div className="flex justify-between"><span>Grundbetrag (Monat)</span><span>{eur(preview.subtotal_base)} €</span></div>
        {Number(preview.subtotal_biweekly_savings) > 0 && (
          <div className="flex justify-between text-blue-700"><span>14-tägig Reduktion</span><span>−{eur(preview.subtotal_biweekly_savings)} €</span></div>
        )}
        {Number(preview.subtotal_admin_fees) > 0 && (
          <div className="flex justify-between"><span>+ Verwaltungsaufschlag (14-tg.)</span><span>+{eur(preview.subtotal_admin_fees)} €</span></div>
        )}
        {Number(preview.subtotal_gema) > 0 && (
          <div className="flex justify-between"><span>+ GEMA-Kopierlizenz</span><span>+{eur(preview.subtotal_gema)} €</span></div>
        )}
        {Number(preview.subtotal_adult_surcharge) > 0 && (
          <div className="flex justify-between"><span>+ Erwachsenenzuschlag</span><span>+{eur(preview.subtotal_adult_surcharge)} €</span></div>
        )}
        {Number(preview.subtotal_social_fund) > 0 && (
          <div className="flex justify-between"><span>+ Sozialfonds</span><span>+{eur(preview.subtotal_social_fund)} €</span></div>
        )}
        {Number(preview.subtotal_family_discount) > 0 && (
          <div className="flex justify-between text-rose-700"><span>− Familien-/Mehrfächerermäßigung</span><span>−{eur(preview.subtotal_family_discount)} €</span></div>
        )}
        {Number(preview.subtotal_student_discount) > 0 && (
          <div className="flex justify-between text-rose-700"><span>− Nachweis-Rabatte</span><span>−{eur(preview.subtotal_student_discount)} €</span></div>
        )}
        <div className="flex justify-between text-lg font-bold pt-2 border-t text-emerald-700">
          <span>Gesamtbetrag</span>
          <span data-testid="invoice-grand-total">{eur(preview.grand_total)} €</span>
        </div>
      </div>

      {(() => {
        const r = preview.rules_applied || {};
        const strategy = r.discount_strategy === 'two_stage'
          ? 'Mehrfächer- + Geschwisterermäßigung (vom Höchstbeitrag abwärts)'
          : r.discount_strategy === 'single'
            ? 'Familienrabatt (eine Staffel über alle Belegungen)'
            : null;
        const tiers = Array.isArray(r.family_tiers_pct) && r.family_tiers_pct.length
          ? r.family_tiers_pct.map((p) => `${Number(p)} %`).join(' / ')
          : null;
        const bits = [];
        if (strategy) bits.push(strategy);
        if (tiers) bits.push(`Staffel: ${tiers}`);
        if (r.gema_per_enrollment_eur != null) {
          bits.push(`GEMA ${formatDeNumber(r.gema_per_enrollment_eur)} € je Belegung`);
        }
        if (r.stand) bits.push(`Stand ${r.stand}`);
        if (!bits.length) return null;
        return (
          <p className="text-xs text-slate-500" data-testid="invoice-rules-summary">
            {bits.join(' · ')}
            {r.source_url && (
              <>
                {' · '}
                <a href={r.source_url} target="_blank" rel="noreferrer" className="underline hover:text-slate-700">
                  Preise &amp; AGB
                </a>
              </>
            )}
          </p>
        );
      })()}
    </div>
  );
}

function InvoicePreviewDialog({ open, onOpenChange, loading, preview }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invoice-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-600" />
            Rechnungs-Vorschau (Familienberechnung)
          </DialogTitle>
          <DialogDescription>
            Berechnet nach Greven-Tarif vom 05/2026 — 14-tägig, GEMA, Familienrabatt.
          </DialogDescription>
        </DialogHeader>
        <InvoicePreviewBody loading={loading} preview={preview} />
      </DialogContent>
    </Dialog>
  );
}
